import { useState, useEffect, useCallback, useRef } from 'react';
import { settingsService } from '../services/settingsService';

/**
 * Hook for lydfeedback ved skanning (Haucon, Teamwork 36334085).
 *
 * Genererer korte toner med Web Audio API (oscillator) — ingen lydfiler, null
 * nettverkslatens. Positiv lyd = kort høy pip; negativ lyd = lavere dobbel-buzz.
 * Tonene er bevisst svært ulike så de ikke kan forveksles i støyende lagermiljø.
 *
 * Styrt av innstillingen 'picking.scanSoundEnabled' (Profile -> System -> default
 * false, migrasjon 080). Default AV: ingen tenants får lyd uten å aktivere den.
 *
 * AudioContext opprettes lazily ved første avspilling. Skannerens tastetrykk
 * (keyboard wedge) regnes som brukerinteraksjon, så autoplay-policyen tillater
 * avspilling; er konteksten suspendert forsøkes resume() først. Alle feil svelges
 * stille — lyd er tilleggsfeedback og skal aldri forstyrre plukkflyten.
 *
 * Gjenbrukbar: bygget uten plukk-avhengigheter slik at mottak/pakking kan ta den
 * i bruk senere (eventuelt med egen setting-nøkkel som parameter).
 */
export function useScanFeedback(settingKey: string = 'picking.scanSoundEnabled'): {
  playSuccess: () => void;
  playError: () => void;
  soundEnabled: boolean;
} {
  const [soundEnabled, setSoundEnabled] = useState<boolean>(false);
  const audioContextRef = useRef<AudioContext | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadSetting = async () => {
      try {
        const setting = await settingsService.getEffectiveSetting(settingKey);
        if (!cancelled && setting && setting.currentValue !== null && setting.currentValue !== undefined) {
          const val = setting.currentValue.toString().toLowerCase();
          setSoundEnabled(val === 'true' || val === '1' || val === 'ja');
        }
      } catch (err) {
        console.warn(`Could not load ${settingKey} setting, sound stays off:`, err);
      }
    };

    loadSetting();

    return () => { cancelled = true; };
  }, [settingKey]);

  /** Hent (og ved behov opprett/gjenoppta) delt AudioContext. Returnerer null ved feil. */
  const getContext = useCallback((): AudioContext | null => {
    try {
      if (!audioContextRef.current) {
        const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (!Ctx) return null;
        audioContextRef.current = new Ctx();
      }
      const ctx = audioContextRef.current;
      if (ctx.state === 'suspended') {
        // resume() er async; tonen planlegges uansett — ved første skann etter
        // sideinnlasting kan den drukne, men alle påfølgende spilles normalt.
        void ctx.resume();
      }
      return ctx;
    } catch {
      return null;
    }
  }, []);

  /** Spill en enkel tone med kort attack/release-envelope (unngår klikk). */
  const playTone = useCallback((
    ctx: AudioContext,
    frequency: number,
    startOffsetMs: number,
    durationMs: number,
    type: OscillatorType
  ) => {
    const start = ctx.currentTime + startOffsetMs / 1000;
    const stop = start + durationMs / 1000;

    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(frequency, start);

    gain.gain.setValueAtTime(0, start);
    gain.gain.linearRampToValueAtTime(0.4, start + 0.01);
    gain.gain.setValueAtTime(0.4, stop - 0.02);
    gain.gain.linearRampToValueAtTime(0, stop);

    oscillator.connect(gain);
    gain.connect(ctx.destination);
    oscillator.start(start);
    oscillator.stop(stop);
  }, []);

  /** Godkjent skann: kort, høy pip (ett tonestøt). */
  const playSuccess = useCallback(() => {
    if (!soundEnabled) return;
    try {
      const ctx = getContext();
      if (!ctx) return;
      playTone(ctx, 1200, 0, 120, 'sine');
    } catch {
      // Lyd er best effort — aldri la feedback-feil påvirke plukkflyten.
    }
  }, [soundEnabled, getContext, playTone]);

  /** Avvist skann: lav dobbel-buzz — tydelig forskjellig fra suksess-pipen. */
  const playError = useCallback(() => {
    if (!soundEnabled) return;
    try {
      const ctx = getContext();
      if (!ctx) return;
      playTone(ctx, 220, 0, 160, 'square');
      playTone(ctx, 220, 240, 160, 'square');
    } catch {
      // Best effort.
    }
  }, [soundEnabled, getContext, playTone]);

  return { playSuccess, playError, soundEnabled };
}
